import type {
  Mediator,
  MediatorTransitionResult,
  TypedEvent,
} from "@/shared/lib/componentHierarchy";
import { createMediator } from "@/shared/lib/componentHierarchy";

export const DEFAULT_PULL_THRESHOLD = 120;
export const DEFAULT_COOLDOWN_MS = 800;
export const MIN_REFRESH_DISPLAY_MS = 800;

const DRAG_RESISTANCE = 0.5;
const MAX_OFFSET = 260;
const SPRING_K = 0.25;
const SPRING_C = 0.15;
const AIR_RESISTANCE = 0.42;
const MAX_VELOCITY = 40;
const TARGET_FRAME_MS = 1000 / 60;

export type PullRefreshPhase =
  | "idle"
  | "pulling"
  | "refreshing"
  | "cooldown"
  | "animating";

export type PullRefreshZone = "top" | "bottom";

export type PullRefreshIndicatorState = {
  progress: number;
  refreshing: boolean;
  opacity: number;
};

type AnimationState = {
  readonly settledPhase: "idle" | "cooldown";
  readonly lastTime: number;
  readonly velocity: number;
};

export type PullRefreshState = {
  readonly phase: PullRefreshPhase;
  readonly activeZone: PullRefreshZone | null;
  readonly engaged: boolean;
  readonly offset: number;
  readonly baselineY: number;
  readonly lastMoveY: number;
  readonly lastMoveTime: number;
  readonly fingerVelocity: number;
  readonly animation: AnimationState | null;
  readonly refreshToken: number;
  readonly refreshCompleted: boolean;
  readonly displayCompleted: boolean;
  readonly pullThreshold: number;
  readonly cooldownMs: number;
  readonly minRefreshDisplayMs: number;
};

export type PullRefreshEvent =
  | {
      readonly type: "pull-refresh/touch-start";
      readonly touchCount: number;
      readonly clientY: number;
    }
  | {
      readonly type: "pull-refresh/touch-move";
      readonly touchCount: number;
      readonly clientY: number;
      readonly scrollY: number;
      readonly innerHeight: number;
      readonly scrollHeight: number;
      readonly now: number;
      readonly canRefresh: boolean;
    }
  | { readonly type: "pull-refresh/touch-end"; readonly now: number }
  | { readonly type: "pull-refresh/touch-cancel"; readonly now: number }
  | { readonly type: "pull-refresh/animation-frame"; readonly now: number }
  | {
      readonly type: "pull-refresh/refresh-settled";
      readonly token: number;
      readonly now: number;
    }
  | {
      readonly type: "pull-refresh/refresh-display-elapsed";
      readonly token: number;
    }
  | {
      readonly type: "pull-refresh/cooldown-elapsed";
      readonly token: number;
    }
  | {
      readonly type: "pull-refresh/configuration-changed";
      readonly pullThreshold: number;
      readonly cooldownMs: number;
    }
  | { readonly type: "pull-refresh/reset-requested" };

export type PullRefreshEffect =
  | { readonly type: "apply-offset"; readonly offset: number }
  | {
      readonly type: "schedule-indicator";
      readonly side: PullRefreshZone;
      readonly state: PullRefreshIndicatorState;
    }
  | { readonly type: "show-refreshing"; readonly side: PullRefreshZone }
  | { readonly type: "clear-indicators" }
  | { readonly type: "prevent-default" }
  | {
      readonly type: "start-animation";
      readonly initialVelocity: number;
    }
  | {
      readonly type: "request-animation-frame";
    }
  | {
      readonly type: "invoke-refresh";
      readonly token: number;
      readonly zone: PullRefreshZone;
    }
  | {
      readonly type: "schedule-refresh-display-clear";
      readonly token: number;
      readonly delayMs: number;
    }
  | {
      readonly type: "schedule-cooldown";
      readonly token: number;
      readonly delayMs: number;
    }
  | { readonly type: "cancel-runtime" };

export interface PullRefreshMediatorOptions {
  readonly pullThreshold?: number;
  readonly cooldownMs?: number;
  readonly minRefreshDisplayMs?: number;
  readonly runEffect?: (effect: PullRefreshEffect) => void;
}

export function isPullRefreshEvent(
  event: TypedEvent,
): event is PullRefreshEvent {
  switch (event.type) {
    case "pull-refresh/touch-start":
      return (
        hasNumberField(event, "touchCount") && hasNumberField(event, "clientY")
      );
    case "pull-refresh/touch-move":
      return (
        hasNumberField(event, "touchCount") &&
        hasNumberField(event, "clientY") &&
        hasNumberField(event, "scrollY") &&
        hasNumberField(event, "innerHeight") &&
        hasNumberField(event, "scrollHeight") &&
        hasNumberField(event, "now") &&
        hasBooleanField(event, "canRefresh")
      );
    case "pull-refresh/touch-end":
    case "pull-refresh/animation-frame":
      return hasNumberField(event, "now");
    case "pull-refresh/refresh-settled":
      return hasNumberField(event, "token") && hasNumberField(event, "now");
    case "pull-refresh/refresh-display-elapsed":
    case "pull-refresh/cooldown-elapsed":
      return hasNumberField(event, "token");
    case "pull-refresh/configuration-changed":
      return (
        hasNumberField(event, "pullThreshold") &&
        hasNumberField(event, "cooldownMs")
      );
    case "pull-refresh/touch-cancel":
      return hasNumberField(event, "now");
    case "pull-refresh/reset-requested":
      return true;
    default:
      return false;
  }
}

export function transitionPullRefresh(
  state: PullRefreshState,
  event: PullRefreshEvent,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  switch (event.type) {
    case "pull-refresh/touch-start":
      if (event.touchCount !== 1) return { status: "unhandled" };
      return handled({
        ...state,
        engaged: false,
        baselineY: event.clientY,
        lastMoveY: 0,
        lastMoveTime: 0,
        fingerVelocity: 0,
      });
    case "pull-refresh/touch-move":
      return transitionTouchMove(state, event);
    case "pull-refresh/touch-end":
      return transitionTouchEnd(state, event.now);
    case "pull-refresh/touch-cancel":
      return transitionTouchCancel(state, event.now);
    case "pull-refresh/animation-frame":
      return transitionAnimationFrame(state, event.now);
    case "pull-refresh/refresh-settled":
      return transitionRefreshSettled(state, event.token, event.now);
    case "pull-refresh/refresh-display-elapsed":
      return transitionRefreshDisplayElapsed(state, event.token);
    case "pull-refresh/cooldown-elapsed":
      return transitionCooldownElapsed(state, event.token);
    case "pull-refresh/configuration-changed":
      return transitionConfigurationChanged(
        state,
        event.pullThreshold,
        event.cooldownMs,
      );
    case "pull-refresh/reset-requested":
      return resetState(state, state.pullThreshold, state.cooldownMs);
  }
}

export function createPullRefreshMediator(
  options: PullRefreshMediatorOptions = {},
): Mediator<PullRefreshState, PullRefreshEvent, PullRefreshEffect> {
  const pullThreshold = options.pullThreshold ?? DEFAULT_PULL_THRESHOLD;
  const cooldownMs = options.cooldownMs ?? DEFAULT_COOLDOWN_MS;
  const minRefreshDisplayMs =
    options.minRefreshDisplayMs ?? MIN_REFRESH_DISPLAY_MS;

  return createMediator<PullRefreshState, PullRefreshEvent, PullRefreshEffect>(
    {
      phase: "idle",
      activeZone: null,
      engaged: false,
      offset: 0,
      baselineY: 0,
      lastMoveY: 0,
      lastMoveTime: 0,
      fingerVelocity: 0,
      animation: null,
      refreshToken: 0,
      refreshCompleted: false,
      displayCompleted: false,
      pullThreshold,
      cooldownMs,
      minRefreshDisplayMs,
    },
    transitionPullRefresh,
    { runEffect: options.runEffect },
  );
}

function transitionTouchMove(
  state: PullRefreshState,
  event: Extract<PullRefreshEvent, { type: "pull-refresh/touch-move" }>,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  if (event.touchCount !== 1 || !event.canRefresh) {
    return { status: "unhandled" };
  }
  if (
    state.phase === "refreshing" ||
    state.phase === "animating" ||
    state.phase === "cooldown"
  ) {
    return { status: "unhandled" };
  }

  const currentY = event.clientY;
  if (!state.engaged) {
    const delta = currentY - state.baselineY;
    if (delta > 0 && isAtTop(event.scrollY)) {
      return handled(
        {
          ...state,
          phase: "pulling",
          activeZone: "top",
          engaged: true,
          baselineY: currentY,
          lastMoveY: currentY,
          lastMoveTime: event.now,
          fingerVelocity: 0,
        },
        [{ type: "prevent-default" }],
      );
    }
    if (
      delta < 0 &&
      isAtBottom(event.scrollY, event.innerHeight, event.scrollHeight)
    ) {
      return handled(
        {
          ...state,
          phase: "pulling",
          activeZone: "bottom",
          engaged: true,
          baselineY: currentY,
          lastMoveY: currentY,
          lastMoveTime: event.now,
          fingerVelocity: 0,
        },
        [{ type: "prevent-default" }],
      );
    }
    return handled({
      ...state,
      baselineY: currentY,
    });
  }

  const zone = state.activeZone;
  if (!zone) return { status: "unhandled" };
  const delta = currentY - state.baselineY;
  if ((zone === "top" && delta <= 0) || (zone === "bottom" && delta >= 0)) {
    return handled(
      {
        ...state,
        phase: "idle",
        activeZone: null,
        engaged: false,
        offset: 0,
        lastMoveY: 0,
        lastMoveTime: 0,
        fingerVelocity: 0,
        animation: null,
      },
      [{ type: "apply-offset", offset: 0 }, { type: "clear-indicators" }],
    );
  }

  const damped = rubberBand(delta);
  const moveDt = event.now - state.lastMoveTime;
  const fingerVelocity =
    state.lastMoveTime > 0 && moveDt > 0 && moveDt < 64
      ? ((currentY - state.lastMoveY) * DRAG_RESISTANCE * TARGET_FRAME_MS) /
        moveDt
      : 0;
  return handled(
    {
      ...state,
      offset: damped,
      lastMoveY: currentY,
      lastMoveTime: event.now,
      fingerVelocity,
    },
    [
      { type: "apply-offset", offset: damped },
      {
        type: "schedule-indicator",
        side: zone,
        state: makeIndicator(calcProgress(damped, state.pullThreshold), false),
      },
      { type: "prevent-default" },
    ],
  );
}

function transitionTouchEnd(
  state: PullRefreshState,
  now: number,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  if (state.phase !== "pulling" || !state.activeZone) {
    return {
      status: "unhandled",
    };
  }

  const zone = state.activeZone;
  const common = {
    ...state,
    engaged: false,
    fingerVelocity: 0,
  };
  if (Math.abs(state.offset) >= state.pullThreshold) {
    const token = state.refreshToken + 1;
    return handled(
      common,
      [
        { type: "show-refreshing", side: zone },
        { type: "invoke-refresh", token, zone },
        {
          type: "schedule-refresh-display-clear",
          token,
          delayMs: state.minRefreshDisplayMs,
        },
      ],
      {
        phase: "refreshing",
        refreshToken: token,
        refreshCompleted: false,
        displayCompleted: false,
        animation: null,
      },
    );
  }

  const sinceLastMove = now - state.lastMoveTime;
  const initialVelocity =
    state.lastMoveTime > 0 && sinceLastMove < 64 ? state.fingerVelocity : 0;
  return handled(
    {
      ...common,
      phase: "animating",
      animation: {
        settledPhase: "idle",
        lastTime: now,
        velocity: initialVelocity,
      },
    },
    [
      { type: "clear-indicators" },
      { type: "start-animation", initialVelocity },
    ],
  );
}

function transitionTouchCancel(
  state: PullRefreshState,
  now: number,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  if (state.phase !== "pulling") return { status: "unhandled" };
  return handled(
    {
      ...state,
      engaged: false,
      phase: "animating",
      fingerVelocity: 0,
      animation: {
        settledPhase: "idle",
        lastTime: now,
        velocity: 0,
      },
    },
    [
      { type: "clear-indicators" },
      { type: "start-animation", initialVelocity: 0 },
    ],
  );
}

function transitionAnimationFrame(
  state: PullRefreshState,
  now: number,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  const animation = state.animation;
  if (
    !animation ||
    (state.phase !== "animating" && state.phase !== "cooldown")
  ) {
    return { status: "unhandled" };
  }

  const rawDt = now - animation.lastTime;
  const dt = Math.min(Math.max(rawDt, 1), 64) / TARGET_FRAME_MS;
  const displacement = Math.abs(state.offset);
  if (displacement < 0.5 && Math.abs(animation.velocity) < 0.5) {
    return handled(
      {
        ...state,
        phase: animation.settledPhase,
        activeZone: animation.settledPhase === "idle" ? null : state.activeZone,
        offset: 0,
        animation: null,
      },
      [{ type: "apply-offset", offset: 0 }],
    );
  }

  const direction = -Math.sign(state.offset);
  const acceleration =
    direction * SPRING_K * (displacement + SPRING_C) -
    AIR_RESISTANCE * animation.velocity;
  const velocity = clampVelocity(animation.velocity + acceleration * dt);
  const newPosition = state.offset + velocity * dt;
  const crossed =
    (state.offset > 0 && newPosition <= 0) ||
    (state.offset < 0 && newPosition >= 0);
  if (crossed) {
    return handled(
      {
        ...state,
        phase: animation.settledPhase,
        activeZone: animation.settledPhase === "idle" ? null : state.activeZone,
        offset: 0,
        animation: null,
      },
      [{ type: "apply-offset", offset: 0 }],
    );
  }

  return handled(
    {
      ...state,
      offset: newPosition,
      animation: {
        ...animation,
        lastTime: now,
        velocity,
      },
    },
    [
      { type: "apply-offset", offset: newPosition },
      { type: "request-animation-frame" },
    ],
  );
}

function transitionRefreshSettled(
  state: PullRefreshState,
  token: number,
  now: number,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  if (state.phase !== "refreshing" || state.refreshToken !== token) {
    return { status: "unhandled" };
  }
  const effects: PullRefreshEffect[] = [
    { type: "start-animation", initialVelocity: 0 },
    {
      type: "schedule-cooldown",
      token,
      delayMs: state.cooldownMs,
    },
  ];
  if (state.displayCompleted) effects.push({ type: "clear-indicators" });
  return handled(
    {
      ...state,
      phase: "cooldown",
      refreshCompleted: true,
      animation: {
        settledPhase: "cooldown",
        lastTime: now,
        velocity: 0,
      },
    },
    effects,
  );
}

function transitionRefreshDisplayElapsed(
  state: PullRefreshState,
  token: number,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  if (state.refreshToken !== token || state.displayCompleted) {
    return { status: "unhandled" };
  }
  return handled(
    {
      ...state,
      displayCompleted: true,
    },
    state.refreshCompleted ? [{ type: "clear-indicators" }] : undefined,
  );
}

function transitionCooldownElapsed(
  state: PullRefreshState,
  token: number,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  if (state.phase !== "cooldown" || state.refreshToken !== token) {
    return { status: "unhandled" };
  }
  if (state.animation) {
    return handled({
      ...state,
      phase: "animating",
      activeZone: null,
      animation: {
        ...state.animation,
        settledPhase: "idle",
      },
    });
  }
  return handled({
    ...state,
    phase: "idle",
    activeZone: null,
    animation: null,
  });
}

function transitionConfigurationChanged(
  state: PullRefreshState,
  pullThreshold: number,
  cooldownMs: number,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  if (
    state.pullThreshold === pullThreshold &&
    state.cooldownMs === cooldownMs
  ) {
    return handled(state);
  }
  const reset = resetState(state, pullThreshold, cooldownMs);
  if (reset.status !== "handled") return reset;
  return reset;
}

function resetState(
  state: PullRefreshState,
  pullThreshold: number,
  cooldownMs: number,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  return handled(
    {
      ...state,
      phase: "idle",
      activeZone: null,
      engaged: false,
      offset: 0,
      baselineY: 0,
      lastMoveY: 0,
      lastMoveTime: 0,
      fingerVelocity: 0,
      animation: null,
      refreshCompleted: false,
      displayCompleted: false,
      pullThreshold,
      cooldownMs,
    },
    [
      { type: "cancel-runtime" },
      { type: "apply-offset", offset: 0 },
      { type: "clear-indicators" },
    ],
  );
}

function handled(
  state: PullRefreshState,
  effects?: readonly PullRefreshEffect[],
  override?: Partial<PullRefreshState>,
): MediatorTransitionResult<PullRefreshState, PullRefreshEffect> {
  return {
    status: "handled",
    state: override ? { ...state, ...override } : state,
    ...(effects ? { effects } : {}),
  };
}

function calcProgress(offset: number, threshold: number): number {
  return Math.min(Math.max(0, (Math.abs(offset) / threshold) * 100), 100);
}

function calcOpacity(progress: number, refreshing: boolean): number {
  if (refreshing) return 1;
  return Math.max(0, progress - 10) / 100;
}

function makeIndicator(
  progress: number,
  refreshing: boolean,
): PullRefreshIndicatorState {
  return {
    progress,
    refreshing,
    opacity: calcOpacity(progress, refreshing),
  };
}

function rubberBand(raw: number): number {
  const sign = Math.sign(raw);
  const abs = Math.abs(raw) * DRAG_RESISTANCE;
  return sign * Math.min(abs, MAX_OFFSET);
}

function clampVelocity(value: number): number {
  return Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, value));
}

function isAtTop(scrollY: number): boolean {
  return scrollY <= 0;
}

function isAtBottom(
  scrollY: number,
  innerHeight: number,
  scrollHeight: number,
): boolean {
  return scrollY + innerHeight >= scrollHeight - 1;
}

function hasNumberField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "number";
}

function hasBooleanField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "boolean";
}
